import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  Outlet,
  Route,
  Routes,
  useBlocker,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router';
import {
  ASSET_KINDS,
  ASSET_PDF_MAX_BYTES,
  MARKET_CODES,
  PDF_KINDS,
  assetMeta,
  fileSize,
  filterAssets,
  storageSize,
  normTag,
  parseVideoUrl,
  providerName,
  tagCounts,
  type AssetFilter,
  type VideoProvider,
} from '@wr/shared';
import {
  ApiError,
  del,
  downloadFile,
  errorMessage,
  get,
  patch,
  post,
  putFile,
  uploadFile,
} from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import {
  Dialog,
  EmptyState,
  Field,
  ManyOfChips,
  Notice,
  PageHead,
  SelectField,
  TextAreaField,
} from '../../components/ui.js';
import { LISTS } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import {
  dropItem,
  keepItem,
  keptBytes,
  keptItems,
  offlineState,
  offlineVersion,
  playableUrl,
  reconcile,
  thumbUrl,
  type KeptItem,
  type OfflineState,
} from '../../offline/media.js';
import { useOnline } from '../../offline/network.js';
import { useSignedIn } from '../../session.js';
import { shortDate } from '../leads/LeadSheet.js';
import { OfflineCopy } from './OfflineCopy.js';
import { OfflineWindow } from './OfflineWindow.js';
import { firstPageThumbnail } from './pdf.js';
import { PdfOverlay, VideoOverlay, Viewer } from './Viewers.js';

/** An item of the library, as the API gives it. */
export interface Asset {
  id: string;
  format: 'video' | 'pdf';
  kind: string;
  title: string;
  lang: string;
  products: string[];
  tags: string[];
  description: string | null;
  url: string | null;
  provider: VideoProvider | null;
  video_id: string | null;
  video_hash: string | null;
  file_name: string | null;
  size_bytes: number | null;
  has_file: boolean;
  has_thumbnail: boolean;
  /** Round 2: an MP4 kept on devices for shows without network. */
  has_offline?: boolean;
  offline_size_bytes?: number | null;
  offline_version?: string | null;
  offline_added_at?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  created_by_name: string | null;
}

interface ContentContext {
  assets: Asset[] | null;
  reload(): void;
  /** Round 2: what this device keeps for shows without network. */
  kept: Record<string, KeptItem>;
  refreshKept(): void;
  setTag(tag: string): void;
  uploads: { files: File[]; set(files: File[]): void };
}

const LANGUAGES: Record<string, string> = Object.fromEntries(
  LISTS.contentLanguages.map((l) => [l.id, l.label]),
);
const PRODUCTS = MARKET_CODES.map((m) => ({ value: m, label: m }));
/** The library as last seen, for opening it without network (round 2). */
const LIBRARY = 'contentLibrary';

/** On a tile: kept on this device, a new version to download, or no offline copy (round 2). */
function OfflineBadge({ state }: { state: OfflineState }) {
  if (state === 'ready')
    return (
      <span className="offline-badge" title="Available offline on this device">
        <Icon name="offline" />
        Offline
      </span>
    );
  if (state === 'update') return <span className="offline-badge upd">Update available</span>;
  return null;
}

/**
 * Content (brief section 7.4, docs/prototype.html), mounted at /content/*:
 * the library of approved videos (Vimeo or YouTube, added by hand) and
 * PDFs: search, type, language and product filters, tags, the item, Play
 * video and Show on screen. Admins add, edit and delete. It needs the
 * network.
 */
export function ContentScreen() {
  return (
    <Routes>
      <Route element={<Library />}>
        <Route index element={null} />
        <Route path="new-video" element={<VideoWindow />} />
        <Route path="upload" element={<UploadWindow />} />
        <Route path=":id" element={<AssetWindow />} />
        <Route path=":id/edit" element={<EditWindow />} />
        <Route path=":id/play" element={<Player />} />
        <Route path=":id/show" element={<PdfViewer />} />
        <Route path="offline" element={<OfflineWindow />} />
      </Route>
    </Routes>
  );
}

const EMPTY: AssetFilter = { q: '', kind: '', lang: '', product: '', tags: [] };

function Library() {
  const { can, refresh } = useSignedIn();
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState<AssetFilter>(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const manage = can('content.manage');
  const { user } = useSignedIn();
  const { store } = useOffline();
  const online = useOnline();
  const [kept, setKept] = useState<Record<string, KeptItem>>({});
  const [offlineList, setOfflineList] = useState(false);
  // Round 2: several videos and PDFs ticked, then one package with them (prototype: .pick, .selbar),
  // or kept on this device for shows without network.
  const pick = true;
  const packages = can('packages.create');
  const [selected, setSelected] = useState<string[]>([]);
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  useEffect(() => {
    let current = true;
    get<{ items: Asset[] }>('/content')
      .then(async ({ items }) => {
        if (!current) return;
        setError(null);
        setOfflineList(false);
        setAssets(items);
        // Round 2: the library opens without network too, and the library's rules apply
        // to what this device keeps (deleted items leave, new versions are only noted).
        if (store) {
          await store.setMeta(LIBRARY, items).catch(() => undefined);
          const now = await reconcile(store, user.id, items).catch(() => null);
          if (current && now) setKept(now);
        }
      })
      .catch(async (err: unknown) => {
        if (err instanceof ApiError && err.status === 401) return void refresh();
        const saved = await store?.getMeta<Asset[]>(LIBRARY).catch(() => null);
        if (!current) return;
        if (saved) {
          setOfflineList(true);
          setAssets(saved);
        } else setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [tick, refresh, store, user.id]);

  useEffect(() => {
    if (store) void keptItems(store).then(setKept);
  }, [store, tick]);

  // A thumbnail fetched by the worker shows a moment after a video is added.
  const waiting = assets?.some((a) => a.format === 'video' && !a.has_thumbnail) ?? false;
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setTick((t) => t + 1), 10_000);
    return () => clearTimeout(timer);
  }, [waiting, assets]);

  const list = useMemo(() => (assets ? filterAssets(assets, filter) : null), [assets, filter]);
  const tags = useMemo(() => (assets ? tagCounts(assets) : []), [assets]);
  const active = filter.q || filter.kind || filter.lang || filter.product || filter.tags.length > 0;
  const set = (change: Partial<AssetFilter>) => setFilter({ ...filter, ...change });
  const toggleTag = (tag: string) =>
    set({
      tags: filter.tags.includes(tag)
        ? filter.tags.filter((t) => t !== tag)
        : [...filter.tags, tag],
    });

  const context: ContentContext = {
    assets,
    reload: () => setTick((t) => t + 1),
    kept,
    refreshKept: () => void (store && keptItems(store).then(setKept)),
    setTag: (tag) => setFilter({ ...EMPTY, tags: [tag] }),
    uploads: { files, set: setFiles },
  };

  return (
    <>
      <PageHead
        title="Content"
        lede={
          packages
            ? 'Approved videos and PDFs. Tap to show on screen, or select several to send as a package or keep offline.'
            : 'Approved videos and PDFs. Tap to show on screen, or select several to keep offline.'
        }
        actions={
          <button type="button" className="btn sm" onClick={() => navigate('/account#offline')}>
            <Icon name="offline" />
            Offline content
          </button>
        }
      />
      {(offlineList || !online) && (
        <Notice>
          No network: what this device keeps plays and opens; the rest needs the network.
        </Notice>
      )}
      {manage && (
        <div className="add-bar">
          <button type="button" className="btn dark" onClick={() => navigate('new-video')}>
            <Icon name="play" />
            Add a video
          </button>
          <button type="button" className="btn primary" onClick={() => input.current?.click()}>
            <Icon name="pdf" />
            Upload PDF
          </button>
          <input
            ref={input}
            type="file"
            multiple
            accept="application/pdf"
            hidden
            aria-label="Choose PDF files"
            onChange={(e) => {
              const chosen = [...(e.target.files ?? [])];
              e.target.value = '';
              if (chosen.length === 0) return;
              setFiles(chosen);
              navigate('upload');
            }}
          />
        </div>
      )}
      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search title, tag, product"
          aria-label="Search content"
          value={filter.q}
          onChange={(e) => set({ q: e.target.value })}
        />
        <select
          aria-label="Type"
          value={filter.kind}
          onChange={(e) => set({ kind: e.target.value })}
        >
          <option value="">All types</option>
          {Object.entries(ASSET_KINDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          aria-label="Language"
          value={filter.lang}
          onChange={(e) => set({ lang: e.target.value })}
        >
          <option value="">All languages</option>
          {LISTS.contentLanguages.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Product"
          value={filter.product}
          onChange={(e) => set({ product: e.target.value })}
        >
          <option value="">All products</option>
          {MARKET_CODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      {tags.length > 0 && (
        <div className="tagrow" role="group" aria-label="Filter by tag">
          {tags.map(([t, n]) => (
            <button
              key={t}
              type="button"
              className="tfilter"
              aria-pressed={filter.tags.includes(t)}
              onClick={() => toggleTag(t)}
            >
              #{t} <span>{n}</span>
            </button>
          ))}
        </div>
      )}
      {active && list && (
        <p className="note content-count">
          {list.length} result{list.length === 1 ? '' : 's'}
          {filter.tags.length > 0
            ? ` tagged ${filter.tags.map((t) => `#${t}`).join(' + ')}`
            : ''}.{' '}
          <button type="button" className="linkbtn" onClick={() => setFilter(EMPTY)}>
            Clear filters
          </button>
        </p>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      {!assets && !error && <div className="spinner" aria-label="Loading" />}
      {list && list.length > 0 && (
        <div className="media-grid">
          {list.map((a) => (
            <article key={a.id} className={`tile${selected.includes(a.id) ? ' sel' : ''}`}>
              {pick && (
                <label className="pick" title="Select">
                  <input
                    type="checkbox"
                    aria-label={`Select ${a.title}`}
                    checked={selected.includes(a.id)}
                    onChange={() => toggle(a.id)}
                  />
                </label>
              )}
              <button
                type="button"
                className="tile-media"
                aria-label={`Open ${a.title}`}
                onClick={() => navigate(a.id)}
              >
                <Thumb
                  asset={a}
                  kept={!online && kept[a.id] ? thumbUrl(user.id, a.id) : undefined}
                />
              </button>
              <div>
                <h3>{a.title}</h3>
                <p className="meta">{assetMeta(a, LANGUAGES)}</p>
                <OfflineBadge state={offlineState(a, kept)} />
                {a.tags.length > 0 && (
                  <p className="ttags">
                    {a.tags
                      .slice(0, 3)
                      .map((t) => `#${t}`)
                      .join(' ')}
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      {assets &&
        list?.length === 0 &&
        (assets.length > 0 ? (
          <EmptyState title="Nothing matches.">
            <button type="button" className="linkbtn" onClick={() => setFilter(EMPTY)}>
              Clear filters
            </button>
          </EmptyState>
        ) : (
          <EmptyState title="No content yet.">
            {manage
              ? 'Add your YouTube or Vimeo videos and upload your PDFs.'
              : 'An administrator adds the videos and PDFs.'}
          </EmptyState>
        ))}
      {selected.length > 0 && (
        <div className="selbar" role="region" aria-label="Selection">
          <span>{selected.length} selected</span>
          {packages && (
            <button
              type="button"
              className="btn sm primary"
              onClick={() => navigate('/packages/new', { state: { assets: selected } })}
            >
              Create package
            </button>
          )}
          <button
            type="button"
            className="btn sm"
            onClick={() => navigate('offline', { state: { ids: selected } })}
          >
            <Icon name="offline" />
            Make available offline
          </button>
          <button type="button" className="btn sm" onClick={() => setSelected([])}>
            Clear
          </button>
        </div>
      )}
      <Outlet context={context} />
    </>
  );
}

/** The thumbnail, or the prototype's tile while there is none. */
export function Thumb({ asset: a, kept }: { asset: Asset; kept?: string }) {
  // Round 2: without network, the thumbnail kept on this device with the item.
  const src = kept ?? `/api/content/${a.id}/thumbnail?v=${encodeURIComponent(a.updated_at)}`;
  if (a.format === 'video') {
    return a.has_thumbnail ? (
      <span className="thumb-wrap">
        <img className="thumb-img" src={src} alt="" loading="lazy" />
        <span className="playdot" />
      </span>
    ) : (
      <span className="vtile">
        <span className="prov">{providerName(a.provider).toUpperCase()}</span>
        <span className="vt">{a.title}</span>
        <span className="playdot" />
      </span>
    );
  }
  return a.has_thumbnail ? (
    <img className="thumb-img top" src={src} alt="" loading="lazy" />
  ) : (
    <span className={`pdftile k-${a.kind}`}>
      <b>PDF</b>
      <span>{a.title}</span>
    </span>
  );
}

function useAsset() {
  const { id = '' } = useParams();
  const ctx = useOutletContext<ContentContext>();
  return { id, ctx, asset: ctx.assets?.find((a) => a.id === id) ?? null };
}

/** The item (prototype: openAsset): Play video or Show on screen; Edit, Delete, Download. */
function AssetWindow() {
  const { user, can } = useSignedIn();
  const { id, ctx, asset: a } = useAsset();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => navigate('/content', { replace: true });
  if (!ctx.assets) return null;
  if (!a) {
    return (
      <Dialog small title="Content" onClose={close}>
        <Notice kind="error">This item does not exist, or it was deleted.</Notice>
      </Dialog>
    );
  }
  const manage = can('content.manage');
  const video = a.format === 'video';

  async function remove() {
    try {
      await del(`/content/${id}`);
      toast('Deleted.');
      ctx.reload();
      close();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <Dialog
      title={a.title}
      onClose={close}
      footer={
        <>
          {manage && (
            <>
              <button type="button" className="btn danger left" onClick={() => setConfirm(true)}>
                Delete
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => navigate('edit', { replace: true })}
              >
                Edit
              </button>
            </>
          )}
          {!video && (
            <button
              type="button"
              className="btn"
              onClick={() =>
                void downloadFile(`/content/${id}/file?download=1`).catch((err: unknown) =>
                  setError(errorMessage(err)),
                )
              }
            >
              Download
            </button>
          )}
          {can('packages.create') && (
            <button
              type="button"
              className="btn dark"
              onClick={() => navigate('/packages/new', { state: { assets: [id] } })}
            >
              Send in a package
            </button>
          )}
        </>
      }
    >
      <div className="asset-media">
        <Thumb asset={a} />
      </div>
      <div>
        <p className="meta asset-meta">
          {assetMeta(a, LANGUAGES)}
          {a.file_name ? `, ${a.file_name}` : ''}
        </p>
        {a.products.map((p) => (
          <span key={p} className="tag">
            {p}
          </span>
        ))}
        <div>
          {a.tags.map((t) => (
            <button
              key={t}
              type="button"
              className="ttag-btn"
              onClick={() => {
                ctx.setTag(t);
                close();
              }}
            >
              #{t}
            </button>
          ))}
        </div>
      </div>
      {a.description && <p className="asset-desc">{a.description}</p>}
      {video ? (
        <button type="button" className="btn primary huge" onClick={() => navigate('play')}>
          <Icon name="play" />
          Play video
        </button>
      ) : (
        <button type="button" className="btn primary huge" onClick={() => navigate('show')}>
          <Icon name="pdf" />
          Show on screen
        </button>
      )}
      <OfflineRow asset={a} kept={ctx.kept} onChanged={ctx.refreshKept} />
      {video && manage && <OfflineCopy asset={a} onChanged={ctx.reload} />}
      <p className="note">
        Added {shortDate(a.created_at)}
        {a.created_by_name
          ? ` by ${a.created_by_name}${a.created_by === user.id ? ' (you)' : ''}`
          : ''}
      </p>
      {error && <Notice kind="error">{error}</Notice>}
      {confirm && (
        <Dialog
          small
          title="Delete this item?"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => setConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void remove()}>
                Delete
              </button>
            </>
          }
        >
          <p className="note">
            "{a.title}" will be removed from the library.
            {video ? ` The video stays on ${providerName(a.provider)}.` : ''}
          </p>
        </Dialog>
      )}
    </Dialog>
  );
}

/**
 * On an item (round 2): kept on this device or not, with Make available
 * offline, Update (with its size, only when the person asks) or Remove
 * from this device; a video without an offline copy says it plays only
 * with network.
 */
function OfflineRow({
  asset: a,
  kept,
  onChanged,
}: {
  asset: Asset;
  kept: Record<string, KeptItem>;
  onChanged(): void;
}) {
  const { user } = useSignedIn();
  const { store } = useOffline();
  const online = useOnline();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const state = offlineState(a, kept);
  const size = offlineVersion(a)?.size ?? 0;

  async function update() {
    if (!store) return;
    setBusy(0);
    setError(null);
    try {
      await keepItem(store, user.id, a, setBusy);
      toast('Updated on this device.');
      onChanged();
    } catch {
      setError('Not downloaded: check the network and try again.');
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!store) return;
    await dropItem(store, user.id, a.id);
    toast('Removed from this device.');
    onChanged();
  }

  return (
    <div className="offline-row" role="group" aria-label="Offline on this device">
      {state === 'unavailable' && (
        <p className="note">No offline copy yet: this video plays only with network.</p>
      )}
      {state === 'none' && (
        <button
          type="button"
          className="btn sm"
          disabled={!online}
          onClick={() => navigate('/content/offline', { state: { ids: [a.id] } })}
        >
          <Icon name="offline" />
          Make available offline ({storageSize(size)})
        </button>
      )}
      {(state === 'ready' || state === 'update') && (
        <>
          <span className="offline-badge">
            <Icon name="offline" />
            Available offline on this device
          </span>
          {state === 'update' && (
            <button
              type="button"
              className="btn sm primary"
              disabled={!online || busy !== null}
              onClick={() => void update()}
            >
              {busy !== null
                ? `Downloading ${Math.round((busy / Math.max(1, kept[a.id]?.update?.size ?? 1)) * 100)}%`
                : `Update available: download ${storageSize(kept[a.id]?.update?.size ?? 0)}`}
            </button>
          )}
          <button type="button" className="btn sm" onClick={() => void remove()}>
            Remove from this device
          </button>
        </>
      )}
      {error && <Notice kind="error">{error}</Notice>}
    </div>
  );
}

interface Meta {
  title: string;
  kind: string;
  lang: string;
  products: string[];
  tags: string[];
  description: string;
}

/** Tags: typed, Enter or a comma adds one (prototype: .tagbox). */
function TagBox({ value, onChange }: { value: string[]; onChange(tags: string[]): void }) {
  const { assets } = useOutletContext<ContentContext>();
  const [text, setText] = useState('');
  const listId = useId();
  const add = () => {
    const more = text.split(',').map(normTag).filter(Boolean);
    if (more.length) onChange([...new Set([...value, ...more])]);
    setText('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <fieldset>
      <legend>
        Tags <span className="hint">Type a tag and press Enter. Sellers search with them.</span>
      </legend>
      <div className="tagbox">
        {value.map((t) => (
          <span key={t} className="tchip">
            #{t}
            <button
              type="button"
              aria-label={`Remove tag ${t}`}
              onClick={() => onChange(value.filter((x) => x !== t))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          list={listId}
          placeholder="Add a tag"
          autoComplete="off"
          enterKeyHint="done"
          aria-label="Add a tag"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onBlur={add}
        />
      </div>
      <datalist id={listId}>
        {tagCounts(assets ?? []).map(([t]) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </fieldset>
  );
}

/** Title, type (PDF), language, products, tags, description (prototype: metaFields). */
function MetaFields({
  value,
  onChange,
  errors,
  title = true,
  kind = false,
}: {
  value: Meta;
  onChange(value: Meta): void;
  errors: Partial<Record<keyof Meta, string>>;
  title?: boolean;
  kind?: boolean;
}) {
  const set = <K extends keyof Meta>(key: K, v: Meta[K]) => onChange({ ...value, [key]: v });
  return (
    <>
      {title && (
        <Field
          label="Title"
          mark
          value={value.title}
          error={errors.title}
          onChange={(e) => set('title', e.target.value)}
        />
      )}
      <div className="g2">
        {kind && (
          <SelectField
            label="Type"
            value={value.kind}
            onChange={(e) => set('kind', e.target.value)}
          >
            {PDF_KINDS.map((k) => (
              <option key={k} value={k}>
                {ASSET_KINDS[k]}
              </option>
            ))}
          </SelectField>
        )}
        <SelectField
          label="Language"
          value={value.lang}
          onChange={(e) => set('lang', e.target.value)}
        >
          {LISTS.contentLanguages.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </SelectField>
      </div>
      <ManyOfChips
        label="Products"
        options={PRODUCTS}
        value={value.products}
        onChange={(v) => set('products', v)}
      />
      <TagBox value={value.tags} onChange={(v) => set('tags', v)} />
      <TextAreaField
        label="Description"
        hint="Shown to the prospect"
        value={value.description}
        onChange={(e) => set('description', e.target.value)}
      />
    </>
  );
}

const metaOf = (a?: Asset | null): Meta => ({
  title: a?.title ?? '',
  kind: a?.format === 'pdf' ? a.kind : 'brochure',
  lang: a?.lang ?? LISTS.contentLanguages[0]?.id ?? 'en',
  products: a?.products ?? [],
  tags: a?.tags ?? [],
  description: a?.description ?? '',
});

/** Asks before leaving a form with changes (Back, Cancel, the menu). */
function useLeaveGuard(dirty: boolean, done: React.RefObject<boolean>) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !done.current && currentLocation.pathname !== nextLocation.pathname,
  );
  return blocker.state === 'blocked' ? (
    <Dialog
      small
      title="Close without saving?"
      onClose={() => blocker.reset?.()}
      footer={
        <>
          <button type="button" className="btn" autoFocus onClick={() => blocker.reset?.()}>
            Keep editing
          </button>
          <button type="button" className="btn primary" onClick={() => blocker.proceed?.()}>
            Close without saving
          </button>
        </>
      }
    >
      <p className="note">Your changes are not saved.</p>
    </Dialog>
  ) : null;
}

/** Add a video, or Edit video (prototype: openVideoForm). */
function VideoWindow({ asset }: { asset?: Asset }) {
  const ctx = useOutletContext<ContentContext>();
  const navigate = useNavigate();
  const toast = useToast();
  const formId = useId();
  const initial = useMemo(() => ({ url: asset?.url ?? '', meta: metaOf(asset) }), [asset]);
  const [url, setUrl] = useState(initial.url);
  const [meta, setMeta] = useState<Meta>(initial.meta);
  const [errors, setErrors] = useState<Partial<Record<keyof Meta | 'url', string>>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const done = useRef(false);
  const dirty = url !== initial.url || JSON.stringify(meta) !== JSON.stringify(initial.meta);
  const leave = useLeaveGuard(dirty, done);
  const detected = url.trim() ? parseVideoUrl(url) : null;
  const close = () => navigate(asset ? `/content/${asset.id}` : '/content', { replace: true });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const problems: typeof errors = {};
    if (!detected) problems.url = 'Paste a YouTube or Vimeo link.';
    if (!meta.title.trim()) problems.title = 'Title is required.';
    setErrors(problems);
    if (Object.keys(problems).length > 0 || busy) return;
    setBusy(true);
    setFailed(null);
    const body = {
      url: url.trim(),
      title: meta.title.trim(),
      lang: meta.lang,
      products: meta.products,
      tags: meta.tags,
      description: meta.description.trim() || null,
    };
    try {
      if (asset) await patch(`/content/${asset.id}`, body);
      else await post('/content/videos', { id: newId(), ...body });
      done.current = true;
      toast(asset ? 'Video saved.' : 'Video added.');
      ctx.reload();
      close();
    } catch (err) {
      setFailed(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={asset ? 'Edit video' : 'Add a video'}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={busy}>
            {asset ? 'Save changes' : 'Add video'}
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
        <Field
          label="YouTube or Vimeo link"
          mark
          type="url"
          inputMode="url"
          hint="Unlisted or private links work. Vimeo private links include a code after the number."
          placeholder="https://vimeo.com/... or https://youtu.be/..."
          value={url}
          error={errors.url}
          onChange={(e) => {
            setUrl(e.target.value);
            setErrors({ ...errors, url: undefined });
          }}
        />
        <p className="note" aria-live="polite">
          {url.trim() &&
            (detected ? (
              <span className="badge b-ok">{providerName(detected.provider)} video detected</span>
            ) : (
              <span className="badge b-hot">Not a YouTube or Vimeo link</span>
            ))}
        </p>
        <MetaFields value={meta} onChange={setMeta} errors={errors} />
        <p className="note wrap-intro">The thumbnail comes from Vimeo or YouTube automatically.</p>
        {failed && <Notice kind="error">{failed}</Notice>}
      </form>
      {leave}
    </Dialog>
  );
}

/** Edit: the video's form, or Edit PDF (prototype: openPdfEdit). */
function EditWindow() {
  const { can } = useSignedIn();
  const { ctx, asset } = useAsset();
  const navigate = useNavigate();
  const toast = useToast();
  const formId = useId();
  const initial = useMemo(() => metaOf(asset), [asset]);
  const [meta, setMeta] = useState<Meta>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof Meta, string>>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const done = useRef(false);
  const leave = useLeaveGuard(JSON.stringify(meta) !== JSON.stringify(initial), done);
  if (!ctx.assets) return null;
  const close = () => navigate(asset ? `/content/${asset.id}` : '/content', { replace: true });
  if (!asset || !can('content.manage')) {
    return (
      <Dialog small title="Edit" onClose={close}>
        <Notice kind="error">
          {asset ? 'Only an administrator edits the library.' : 'This item does not exist.'}
        </Notice>
      </Dialog>
    );
  }
  if (asset.format === 'video') return <VideoWindow asset={asset} />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!meta.title.trim()) {
      setErrors({ title: 'Title is required.' });
      return;
    }
    try {
      await patch(`/content/${asset!.id}`, {
        title: meta.title.trim(),
        kind: meta.kind,
        lang: meta.lang,
        products: meta.products,
        tags: meta.tags,
        description: meta.description.trim() || null,
      });
      done.current = true;
      toast('Saved.');
      ctx.reload();
      close();
    } catch (err) {
      setFailed(errorMessage(err));
    }
  }

  return (
    <Dialog
      title="Edit PDF"
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary">
            Save changes
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
        <MetaFields value={meta} onChange={setMeta} errors={errors} kind />
        {failed && <Notice kind="error">{failed}</Notice>}
      </form>
      {leave}
    </Dialog>
  );
}

interface Upload {
  name: string;
  progress: number;
  status: 'up' | 'done' | 'err';
  message?: string;
}

// The file's name, underscores as spaces ("WR-2" keeps its hyphen).
const titleOf = (name: string) =>
  name
    .replace(/\.[^.]+$/, '')
    .replace(/_+/g, ' ')
    .trim();

/** Upload PDF (prototype: openPdfUpload): a title and type per file, the rest shared. */
function UploadWindow() {
  const ctx = useOutletContext<ContentContext>();
  const navigate = useNavigate();
  const formId = useId();
  const files = ctx.uploads.files;
  const [rows, setRows] = useState(() =>
    files.map((f) => ({
      file: f,
      title: titleOf(f.name),
      kind: 'brochure',
      error:
        f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)
          ? 'Only PDF files. Export documents and slides to PDF.'
          : f.size > ASSET_PDF_MAX_BYTES
            ? 'Over 50 MB. Compress the PDF and try again.'
            : null,
    })),
  );
  const [meta, setMeta] = useState<Meta>(metaOf());
  const [uploads, setUploads] = useState<Upload[]>([]);
  const close = () => {
    ctx.uploads.set([]);
    navigate('/content', { replace: true });
  };
  const ok = rows.filter((r) => !r.error);

  async function start(e: React.FormEvent) {
    e.preventDefault();
    const jobs = ok.map((r) => ({ ...r, title: r.title.trim() || titleOf(r.file.name) }));
    setUploads(jobs.map((j) => ({ name: j.file.name, progress: 0, status: 'up' })));
    for (const [i, job] of jobs.entries()) {
      const update = (u: Partial<Upload>) =>
        setUploads((list) => list.map((x, n) => (n === i ? { ...x, ...u } : x)));
      try {
        const id = newId();
        await post('/content/pdfs', {
          id,
          kind: job.kind,
          title: job.title,
          file_name: job.file.name,
          lang: meta.lang,
          products: meta.products,
          tags: meta.tags,
          description: meta.description.trim() || null,
        });
        const thumbnail = await firstPageThumbnail(job.file);
        await uploadFile(`/content/${id}/file`, job.file, (p) => update({ progress: p }));
        if (thumbnail) await putFile(`/content/${id}/thumbnail`, thumbnail).catch(() => null);
        update({ progress: 1, status: 'done' });
      } catch (err) {
        update({ status: 'err', message: errorMessage(err) });
      }
    }
    ctx.reload();
  }

  if (files.length === 0) return null;
  if (uploads.length > 0) {
    const active = uploads.filter((u) => u.status === 'up').length;
    return (
      <div className="uppanel" role="status">
        <header>
          <span>{active ? `Uploading ${active}` : 'Uploads finished'}</span>
          {!active && (
            <button type="button" aria-label="Close" onClick={close}>
              ×
            </button>
          )}
        </header>
        {uploads.map((u) => (
          <div key={u.name} className={`uprow ${u.status}`}>
            <div className="nm">
              <span>{u.name}</span>
              <span>
                {u.status === 'err'
                  ? 'Failed'
                  : u.status === 'done'
                    ? 'Done'
                    : `${Math.round(u.progress * 100)}%`}
              </span>
            </div>
            {u.status === 'err' ? (
              <div className="small">{u.message}</div>
            ) : (
              <div className="bar">
                <i style={{ width: `${Math.round(u.progress * 100)}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Dialog
      title="Upload PDF"
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={ok.length === 0}>
            Upload {ok.length} PDF{ok.length > 1 ? 's' : ''}
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void start(e)}>
        {rows.map((r, i) => (
          <div key={`${r.file.name}-${i}`} className="formsec upload-file">
            <div className="note">
              {r.file.name}, {fileSize(r.file.size)}
            </div>
            {r.error ? (
              <p className="upload-err">{r.error}</p>
            ) : (
              <div className="g2">
                <Field
                  label="Title"
                  value={r.title}
                  onChange={(e) =>
                    setRows(rows.map((x, n) => (n === i ? { ...x, title: e.target.value } : x)))
                  }
                />
                <SelectField
                  label="Type"
                  value={r.kind}
                  onChange={(e) =>
                    setRows(rows.map((x, n) => (n === i ? { ...x, kind: e.target.value } : x)))
                  }
                >
                  {PDF_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {ASSET_KINDS[k]}
                    </option>
                  ))}
                </SelectField>
              </div>
            )}
          </div>
        ))}
        <p className="note">These fields apply to every file in this upload.</p>
        <MetaFields value={meta} onChange={setMeta} errors={{}} title={false} />
      </form>
    </Dialog>
  );
}

/** Play video: the provider's own player; Back closes it. */
function Player() {
  const { ctx, asset } = useAsset();
  const { user } = useSignedIn();
  const online = useOnline();
  const navigate = useNavigate();
  const [found, setFound] = useState<{ id: string; url: string | null } | null>(null);
  // Round 2: online, the provider streams it; without network, the copy kept on this device.
  const wanted = !online && asset && ctx.kept[asset.id] ? asset.id : null;
  useEffect(() => {
    if (!wanted) return;
    let current = true;
    void playableUrl(user.id, wanted).then((url) => current && setFound({ id: wanted, url }));
    return () => {
      current = false;
    };
  }, [wanted, user.id]);
  const local = !wanted ? null : found?.id === wanted ? found.url : undefined;
  if (!ctx.assets || !asset?.provider || !asset.video_id) return null;
  if (!online) {
    return (
      <Viewer title={asset.title} className="player" onClose={() => navigate(-1)}>
        <div className="pstage">
          {local ? (
            <video className="pframe" src={local} controls autoPlay playsInline />
          ) : local === null ? (
            <p className="offline-msg">
              No network, and this video is not on this device. Make it available offline before the
              show.
            </p>
          ) : null}
        </div>
      </Viewer>
    );
  }
  return (
    <VideoOverlay
      title={asset.title}
      video={{ provider: asset.provider, id: asset.video_id, hash: asset.video_hash }}
      onClose={() => navigate(-1)}
    />
  );
}

/** Show on screen: every page of the PDF, with zoom; Back closes it. */
function PdfViewer() {
  const { id, ctx, asset } = useAsset();
  const { user } = useSignedIn();
  const navigate = useNavigate();
  if (!ctx.assets || !asset) return null;
  return (
    <PdfOverlay
      title={asset.title}
      url={`/api/content/${id}/file`}
      // Round 2: the copy kept on this device first (it opens without network).
      load={
        ctx.kept[id]
          ? async () =>
              (await keptBytes(user.id, id)) ??
              (await fetch(`/api/content/${id}/file`).then((r) => r.arrayBuffer()))
          : undefined
      }
      onClose={() => navigate(-1)}
      onDownload={() => void downloadFile(`/content/${id}/file?download=1`)}
    />
  );
}
